import moment from 'moment';
import { PRODUTO_BANANA_ID } from 'utils/variaveisGlobais';
import { toPrice } from 'utils/converters';

export const OPTIONS_TIPO_EMISSAO_CACHOS = [
  { id: 'semanal', descricao: 'Semanal' },
  { id: 'quinzenal', descricao: 'Quinzenal' },
];

export const MARCACAO_CACHOS_PAGE = 1;
export const MARCACAO_CACHOS_PAGE_SIZE = 100;
export const MARCACAO_CACHOS_PAGE_SIZE_OPTIONS = [100, 200, 300];

export const MARCACAO_CACHOS_MODAIS_DEFAULT = {
  update: false,
  remove: false,
};

export function getDefaultMarcacaoFilters() {
  return {
    dateStart: moment().startOf('month').format('YYYY-MM-DD'),
    dateEnd: moment().endOf('month').format('YYYY-MM-DD'),
    talhaoId: undefined,
    codigoEmissao: undefined,
    order_by: 'data',
    order: 'DESC',
    page: MARCACAO_CACHOS_PAGE,
    pageSize: MARCACAO_CACHOS_PAGE_SIZE,
  };
}

function getMarcacaoScopeFilters(
  filters,
  { includeCodigoEmissao = true } = {},
) {
  const scope = {};

  if (filters?.dateStart && filters?.dateEnd) {
    scope.dataInicio = filters.dateStart;
    scope.dataFim = filters.dateEnd;
  }

  if (filters?.talhaoId) {
    scope.talhaoId = filters.talhaoId;
  }

  if (includeCodigoEmissao && filters?.codigoEmissao) {
    scope.codigoEmissao = filters.codigoEmissao;
  }

  return scope;
}

export function isFazendaAtivaReady(fazendaAtiva) {
  return fazendaAtiva?.id === 0 || Boolean(fazendaAtiva?.id);
}

export function getMarcacaoFazendaFilter(fazendaAtiva) {
  if (!isFazendaAtivaReady(fazendaAtiva)) return {};
  return { fazenda: fazendaAtiva.id };
}

export function buildMarcacaoListFilter(safraId, filters, fazendaAtiva) {
  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
    page: filters.page,
    pageSize: filters.pageSize,
    ...getMarcacaoScopeFilters(filters),
    ...(filters.order_by
      ? { order_by: filters.order_by, order: filters.order || 'DESC' }
      : {}),
  };
}

export function buildMarcacaoEmissoesFilter(safraId, filters, fazendaAtiva) {
  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
    ...getMarcacaoScopeFilters(filters, { includeCodigoEmissao: false }),
  };
}

export function getDefaultMarcacaoDashboardFilters() {
  return {
    dateStart: undefined,
    dateEnd: undefined,
    talhaoId: undefined,
    codigoEmissao: undefined,
  };
}

export function buildMarcacaoDashboardFilter(safraId, filters, fazendaAtiva) {
  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
    ...getMarcacaoScopeFilters(filters),
  };
}

export function buildMarcacaoTalhoesReducedFilter(
  safraId,
  fazendaAtiva,
  { filterByFazenda = true } = {},
) {
  if (!safraId) return null;

  if (filterByFazenda && !isFazendaAtivaReady(fazendaAtiva)) return null;

  if (!filterByFazenda) {
    return { safra: safraId };
  }

  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
  };
}

export function isTalhaoMarcacaoSelectivel(talhao) {
  return String(talhao?.nome || '').toUpperCase() !== 'GASTOS GERAIS';
}

export function filterTalhoesMarcacaoSelect(talhoes) {
  return (talhoes || []).filter(isTalhaoMarcacaoSelectivel);
}

export function mapBarEmissaoData(porEmissao) {
  const items = porEmissao || [];

  return {
    categories: items.map((item) => item.codigo),
    data: items.map((item) => Number(item.totalQuantidade || 0)),
  };
}

export function mapDonutEmissaoData(porEmissao) {
  const items = porEmissao || [];
  const total = items.reduce(
    (acc, item) => acc + Number(item.totalQuantidade || 0),
    0,
  );

  if (!total) {
    return { series: [], labels: [] };
  }

  return {
    series: items.map((item) => Number(item.totalQuantidade || 0)),
    labels: items.map(
      (item) => `${item.codigo} - ${Number(item.totalQuantidade || 0)}`,
    ),
  };
}

export function buildMarcacaoResumoFilter(filter) {
  if (!filter?.safra) return {};

  const {
    page: _page,
    pageSize: _pageSize,
    order_by: _orderBy,
    order: _order,
    ...scope
  } = filter;
  return scope;
}

export function hasMarcacaoDateFilter(filters) {
  return Boolean(filters?.dateStart && filters?.dateEnd);
}

export function hasMarcacaoEmissaoFilter(filters) {
  return Boolean(filters?.codigoEmissao);
}

export function getMarcacaoFiltroPeriodoEmissaoHint(hasEmissaoFilter) {
  return hasEmissaoFilter
    ? 'Período e emissão não podem ser usados juntos. Limpe a emissão para filtrar por período.'
    : 'Período e emissão não podem ser usados juntos. Limpe o período para filtrar por emissão.';
}

export function formatMarcacaoDateRangeLabel(
  filters,
  fallback = 'Período filtrado',
  separator = ' → ',
) {
  if (!hasMarcacaoDateFilter(filters)) {
    return fallback;
  }

  const inicio = moment(filters.dateStart).format('DD/MM/YYYY');
  const fim = moment(filters.dateEnd).format('DD/MM/YYYY');
  return `${inicio}${separator}${fim}`;
}

export function getMarcacaoTotalResumoLabel(filters) {
  if (hasMarcacaoEmissaoFilter(filters)) {
    return `Total na emissão ${filters.codigoEmissao}`;
  }

  if (hasMarcacaoDateFilter(filters)) {
    return `Total de ${formatMarcacaoDateRangeLabel(filters, '', ' a ')}`;
  }

  return 'Total filtrado';
}

export function mapEmissaoFilterOptions(emissoes) {
  return (emissoes || []).map((item) => ({
    id: item.codigo,
    descricao: `${item.codigo} (${item.totalQuantidade} cachos)`,
  }));
}

export function mergeMarcacaoDateFilterUpdate(
  old,
  next,
  { resetPage = false } = {},
) {
  const datesChanged =
    next.dateStart !== old.dateStart || next.dateEnd !== old.dateEnd;

  if (datesChanged) {
    return {
      ...next,
      codigoEmissao: undefined,
      ...(resetPage ? { page: MARCACAO_CACHOS_PAGE } : {}),
    };
  }

  return next;
}

export function mergeMarcacaoEmissaoFilterUpdate(
  old,
  option,
  { resetPage = false, restoreListDates = false } = {},
) {
  if (option?.id) {
    return {
      ...old,
      codigoEmissao: option.id,
      dateStart: undefined,
      dateEnd: undefined,
      ...(resetPage ? { page: MARCACAO_CACHOS_PAGE } : {}),
    };
  }

  const defaults = restoreListDates
    ? getDefaultMarcacaoFilters()
    : { dateStart: undefined, dateEnd: undefined };

  return {
    ...old,
    codigoEmissao: undefined,
    dateStart: defaults.dateStart,
    dateEnd: defaults.dateEnd,
    ...(resetPage ? { page: MARCACAO_CACHOS_PAGE } : {}),
  };
}

export function formatTalhaoLabel(talhao) {
  if (!talhao) return '';
  if (talhao.numero) {
    return `${talhao.nome} (${talhao.numero})`;
  }
  return talhao.nome;
}

export function formatTalhaoCodigo(talhao) {
  if (!talhao?.numero) return '—';
  return talhao.numero;
}

export function formatTalhaoNome(talhao) {
  if (!talhao) return '-';
  return talhao.nome;
}

export function formatQuantidadePorHa(value) {
  if (value == null || value === '') return '—';
  return toPrice(value, 2, 2);
}

export function getMelhorMenorRendimentoTalhao(porTalhao) {
  const eligible = (porTalhao || []).filter(
    (item) =>
      Number(item.areaHa) > 0 &&
      item.quantidadePorHa != null &&
      !Number.isNaN(Number(item.quantidadePorHa)),
  );

  if (!eligible.length) {
    return { melhor: null, menor: null };
  }

  const sorted = [...eligible].sort(
    (a, b) => Number(a.quantidadePorHa) - Number(b.quantidadePorHa),
  );

  return {
    melhor: sorted[sorted.length - 1],
    menor: sorted[0],
  };
}

export function formatTalhaoRendimentoTitulo(talhao) {
  if (!talhao) return '—';
  if (talhao.numero) {
    return `${talhao.numero} — ${talhao.nome}`;
  }
  return talhao.nome;
}

export function formatTalhaoRendimentoRodape(talhao) {
  if (!talhao) return null;

  const cachos =
    talhao.quantidade != null ? `${Number(talhao.quantidade)} cachos` : null;
  const area =
    talhao.areaHa != null ? `${toPrice(talhao.areaHa, 1, 1)} ha` : null;

  return [cachos, area].filter(Boolean).join(' · ') || null;
}

export function supportsMarcacaoCachos(safra) {
  return safra?.product?.gloProdutoId === PRODUTO_BANANA_ID;
}

export const HOME_COLHEITA_SHORTCUT_PATH = '/home/colheita';

export function isHomeColheitaShortcutPath(routePath) {
  const normalized = String(routePath || '').replace(/\/$/, '');

  return (
    normalized === HOME_COLHEITA_SHORTCUT_PATH ||
    normalized.endsWith(HOME_COLHEITA_SHORTCUT_PATH)
  );
}

export function getShortcutDisplayForSafra(routePath, routeItem, safra) {
  if (supportsMarcacaoCachos(safra) && isHomeColheitaShortcutPath(routePath)) {
    return {
      title: 'Marcação',
      description: 'Acompanhe a marcação de cachos',
    };
  }

  return {
    title: routeItem?.title || routePath,
    description: routeItem?.description || '',
  };
}

export function isProdutoBanana(produto) {
  if (!produto) return false;
  if (produto.gloProdutoId === PRODUTO_BANANA_ID) return true;
  return String(produto.descricao || '').toUpperCase() === 'BANANA';
}

export function getTipoEmissaoCachosValue(tipoEmissaoField) {
  return tipoEmissaoField?.id || 'semanal';
}

export function getTipoEmissaoCachosOption(tipoEmissao) {
  return (
    OPTIONS_TIPO_EMISSAO_CACHOS.find((item) => item.id === tipoEmissao) ||
    OPTIONS_TIPO_EMISSAO_CACHOS[0]
  );
}

export function shouldIncludeTipoEmissaoCachosOnSafraSave(
  tipoAtual,
  tipoNovo,
  { isUpdate = false } = {},
) {
  if (!isUpdate) return true;

  const next = getTipoEmissaoCachosValue(tipoNovo);

  if (tipoAtual == null || tipoAtual === '') {
    return next !== 'semanal';
  }

  return next !== tipoAtual;
}

export function appendTipoEmissaoCachosToSafraPayload(
  payload,
  formData,
  { tipoEmissaoAtual, isUpdate = false } = {},
) {
  if (!isProdutoBanana(formData?.produto)) return payload;

  if (
    !shouldIncludeTipoEmissaoCachosOnSafraSave(
      tipoEmissaoAtual,
      formData?.tipo_emissao_cachos,
      { isUpdate },
    )
  ) {
    return payload;
  }

  return {
    ...payload,
    tipo_emissao_cachos: getTipoEmissaoCachosValue(formData?.tipo_emissao_cachos),
  };
}

export function getTalhaoSubtitulo(talhao) {
  if (!talhao) return '';
  return (
    talhao.fazendaNome ||
    talhao.fazenda?.nome ||
    talhao.fazenda ||
    talhao.setor ||
    ''
  );
}

export const MARCACAO_CODIGO_EMISSAO_CONFLITO_MSG =
  'O código de emissão já foi utilizado na safra em outro ano. Escolha outra data.';

export function normalizeMarcacaoListContent(response) {
  if (Array.isArray(response)) return response;
  return response?.content || [];
}

export function buildMarcacaoCodigoEmissaoFilter(safra, codigoEmissao) {
  return {
    safra,
    codigoEmissao,
    page: MARCACAO_CACHOS_PAGE,
    pageSize: MARCACAO_CACHOS_PAGE_SIZE,
  };
}

export function isCodigoEmissaoResponseConflito(response) {
  return Boolean(response?.duplicado || response?.conflito);
}

export function hasCodigoEmissaoConflitoAnual(
  marcacoes,
  data,
  { excludeMarcacaoId } = {},
) {
  if (!data || !marcacoes?.length) return false;

  const selectedYear = moment(data).year();
  if (Number.isNaN(selectedYear)) return false;

  return marcacoes.some((item) => {
    if (excludeMarcacaoId != null && item.id === excludeMarcacaoId) {
      return false;
    }
    if (!item?.data) return false;
    return moment(item.data).year() !== selectedYear;
  });
}

export function getCodigoEmissaoConflitoMensagem(
  marcacoes,
  data,
  { excludeMarcacaoId } = {},
) {
  return hasCodigoEmissaoConflitoAnual(marcacoes, data, { excludeMarcacaoId })
    ? MARCACAO_CODIGO_EMISSAO_CONFLITO_MSG
    : null;
}

export function filterTalhoesMarcacao(talhoes, searchTerm) {
  const term = String(searchTerm || '')
    .trim()
    .toLowerCase();

  if (!term) return talhoes;

  return talhoes.filter((talhao) => {
    const searchable = [talhao.nome, talhao.numero, getTalhaoSubtitulo(talhao)]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());

    return searchable.some((value) => value.includes(term));
  });
}
